import React from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import styles from './styles.module.scss';
import { TachoModalHeader } from './TachoModalHeader';
import { Button, JustifyContents, Alignment, Icon } from 'tacho-ui';
import { FormStatusComponent } from '../FormStatus/formStatusComponent';
import { TachoFormWarning } from '../TachoFormError';

interface TachoModalProps {
    isOpen: boolean;
    title: string | JSX.Element;
    submitLabelToTranslate?: string;
    cancelLabelToTranslate?: string;
    height?: 'full' | 'auto';
    icon?: Icon;
    submitIcon?: Icon;
    cancelIcon?: Icon;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
    bodyClassName?: string;
    errorToTranslate?: string;
    errorTranslated?: string;
    warningToTranslate?: string;
    isReadOnly?: boolean;
    isPending?: boolean;
    isLoading?: boolean;
    hideFooter?: boolean;
    hideSubmitButton?: boolean;
    disableSubmitButton?: boolean;
    customFooter?: (props: any) => JSX.Element;
    letfPartFooter?: (props: any) => JSX.Element;
    customFooterProps?: any;
    leftPartFooterProps?: any;
    titleHorizontalLine?: boolean;
    saveAndNextButton?: boolean;
    onToggle?: () => void;
    onCancel?: () => void;
    onClosed?: () => void;
    onSubmit?: () => void;
    onSubmitAndNext?: () => void;
}

export function TachoModal(props: React.PropsWithChildren<TachoModalProps>): JSX.Element {
    const { formatMessage } = useIntl();

    const contentClassName =
        props.height === 'full' ? styles.fullHeight : props.height === 'auto' ? styles.autoHeight : styles.fullHeight;

    return (
        <Modal
            size={props.size || 'xl'}
            isOpen={props.isOpen}
            className={`createUpdateModal ${props.className || ''}`}
            contentClassName={contentClassName}
            backdrop="static"
            onClosed={props.onClosed}
            fade={false}
        >
            <TachoModalHeader
                title={props.title}
                icon={props.icon}
                isReadOnly={props.isReadOnly}
                isPending={props.isPending}
                titleHorizontalLine={props.titleHorizontalLine}
                onToggle={() => !props.isPending && props.onToggle && props.onToggle()}
            />
            <ModalBody className={props.bodyClassName || ''}>{props.children}</ModalBody>
            {!props.hideFooter && (
                <ModalFooter>
                    {props.customFooter ? (
                        <props.customFooter {...props.customFooterProps} />
                    ) : (
                        <>
                            <div className="leftFooter">
                                {props.letfPartFooter ? props.letfPartFooter(props.leftPartFooterProps) : <></>}
                            </div>
                            <div className="centerFooter"></div>
                            <div className="rightFooter">
                                <FormStatusComponent
                                    formPending={props.isPending || false}
                                    formErrorMessageToTranslate={props.errorToTranslate || ''}
                                    formErrorMessageTranslated={props.errorTranslated}
                                />
                                {props.warningToTranslate && <TachoFormWarning warningToTranslate={props.warningToTranslate} />}
                                {props.onToggle && (props.isReadOnly || props.isLoading) ? (
                                    <Button
                                        justify={JustifyContents.center}
                                        align={Alignment.center}
                                        gap={4}
                                        text={formatMessage({ id: props.cancelLabelToTranslate || 'tachoModalClose' })}
                                        icon={props.cancelIcon || Icon.Clear}
                                        className="secondary"
                                        disabled={props.isPending}
                                        onClick={() => props.onToggle?.()}
                                    />
                                ) : (
                                    <Button
                                        justify={JustifyContents.center}
                                        align={Alignment.center}
                                        gap={4}
                                        text={formatMessage({ id: props.cancelLabelToTranslate || 'tachoModalCancel' })}
                                        icon={props.cancelIcon || Icon.Clear}
                                        className="secondary"
                                        disabled={props.isPending}
                                        onClick={() => props.onToggle?.()}
                                    />
                                )}
                                {props.saveAndNextButton && (
                                    <Button
                                        justify={JustifyContents.center}
                                        align={Alignment.center}
                                        gap={4}
                                        text={formatMessage({ id: 'tachoModelSaveAndNext' })}
                                        icon={props.submitIcon || Icon.Save}
                                        className="secondary"
                                        disabled={
                                            props.disableSubmitButton || props.isReadOnly || props.isPending || props.isLoading
                                        }
                                        loading={props.isPending}
                                        onClick={() => props.onSubmitAndNext?.()}
                                    />
                                )}
                                {!props.hideSubmitButton && (
                                    <Button
                                        justify={JustifyContents.center}
                                        align={Alignment.center}
                                        gap={4}
                                        text={formatMessage({ id: props.submitLabelToTranslate || 'tachoModalSave' })}
                                        icon={props.submitIcon || Icon.Save}
                                        className="secondary"
                                        disabled={
                                            props.disableSubmitButton || props.isReadOnly || props.isPending || props.isLoading
                                        }
                                        loading={props.isPending}
                                        onClick={() => props.onSubmit?.()}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </ModalFooter>
            )}
        </Modal>
    );
}
